import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useTrip } from '../state/trip';
import { Empty, PageHeader } from '../components/ui';

export default function PassportPage() {
  const { stamps, unstamp } = useTrip();
  const routes = useQuery({ queryKey: ['routes'], queryFn: api.routes });
  const details = useQuery({
    queryKey: ['routes-detail', routes.data?.map((r) => r.slug)],
    queryFn: () => Promise.all(routes.data!.map((r) => api.route(r.slug))),
    enabled: !!routes.data?.length,
  });

  const entries = Object.entries(stamps).sort((a, b) => b[1].at.localeCompare(a[1].at));

  return (
    <>
      <PageHeader eyebrow="Saved on this device" title="Backroad passport">
        Stamp every bar and bite you stop at from its page. Finish every stop on a route to complete it.
      </PageHeader>

      <div className="mx-auto max-w-7xl px-4 pb-14 sm:px-6">
        {details.data && (
          <div className="mb-8 flex flex-wrap gap-3">
            {details.data.map((r) => {
              const done = r.stops.filter((s) => stamps[s.place.id]).length;
              const pct = Math.round((done / Math.max(1, r.stops.length)) * 100);
              return (
                <Link
                  key={r.slug}
                  to={`/routes/${r.slug}`}
                  className={`card flex min-w-56 flex-col gap-1.5 p-4 text-cream no-underline hover:text-cream ${done === r.stops.length ? 'border-amber' : ''}`}
                >
                  <b>{r.name}</b>
                  <span className="text-sm text-muted tabular-nums">
                    {done} of {r.stops.length} stamped{done === r.stops.length ? ' · complete!' : ''}
                  </span>
                  <span className="h-1.5 overflow-hidden rounded bg-line" aria-hidden>
                    <span className="block h-1.5 bg-amber" style={{ width: `${pct}%` }} />
                  </span>
                </Link>
              );
            })}
          </div>
        )}

        {entries.length === 0 ? (
          <Empty>
            No stamps yet. Open any stop and tap “Stamp passport” when you&apos;re there. <Link to="/routes">Pick a route</Link> to get going.
          </Empty>
        ) : (
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-4">
            {entries.map(([id, s]) => (
              <li key={id} className="flex aspect-square -rotate-3 flex-col items-center justify-center gap-1 rounded-full border-[3px] border-double border-amber p-4 text-center">
                <b className="text-sm leading-tight text-amber-hi">{s.name}</b>
                <small className="text-[11px] uppercase tracking-[0.1em] text-amber">{s.place}</small>
                <small className="text-[11px] text-muted">{new Date(s.at).toLocaleDateString()}</small>
                <button type="button" className="mt-1 text-[12px] text-dim underline" onClick={() => unstamp(id)}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
